/**
 * ActivationQueuePanel
 *
 * Live activation queue inspector for debugging and monitoring.
 * Shows all in-flight activations with:
 *   - Phase labels (warmup / hold / queued wait / handover)
 *   - Live elapsed timers (1s tick)
 *   - Stuck-state highlighting (orange warn → red critical)
 *   - Per-activation state transition event log
 *   - WebSocket-driven instant updates + 2s polling fallback
 *
 * Used on the Port Status page (/port-status); not on the user-facing gateway page.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../../api';
import { PortActivationBadge, activationEntryFromBatchStatus } from '../../../shared/portActivation';

const POLL_MS = 2000;
const STUCK_WARN_MS = 30_000;
const STUCK_CRIT_MS = 120_000;
const QUEUE_WARN_MS = 300_000;
const QUEUE_CRIT_MS = 600_000;
const MAX_LOG_ENTRIES = 40;

function fmtElapsed(ms) {
  if (ms == null || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${rem}s`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour12: false });
  } catch {
    return '—';
  }
}

function parseUTC(iso) {
  if (!iso) return null;
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const t = Date.parse(s);
  return isNaN(t) ? null : t;
}

function getPhase(item) {
  const now = Date.now();
  switch (item.status) {
    case 'queued':
      return { label: 'Waiting in queue', color: '#6c757d', emoji: '⏳' };
    case 'activating': {
      const warmupUntil = parseUTC(item.warmup_until);
      if (warmupUntil && now < warmupUntil)
        return { label: 'Warmup (yellow)', color: '#fd7e14', emoji: '🟡' };
      return { label: 'Confirming active', color: '#0dcaf0', emoji: '🔵' };
    }
    case 'active':
      return { label: 'Active (holding)', color: '#198754', emoji: '🟢' };
    case 'handing_over':
      return { label: 'Handover countdown', color: '#ffc107', emoji: '🔁' };
    default:
      return { label: item.status || '—', color: '#adb5bd', emoji: '⚪' };
  }
}

function getStuck(item, elapsedMs) {
  if (item.status === 'activating') {
    if (elapsedMs >= STUCK_CRIT_MS) return 'crit';
    if (elapsedMs >= STUCK_WARN_MS) return 'warn';
  }
  if (item.status === 'queued') {
    if (elapsedMs >= QUEUE_CRIT_MS) return 'crit';
    if (elapsedMs >= QUEUE_WARN_MS) return 'warn';
  }
  return null;
}

function rowBg(stuck) {
  if (stuck === 'crit') return '#fff0f0';
  if (stuck === 'warn') return '#fffbe6';
  return 'transparent';
}

function CountdownCell({ item, nowMs }) {
  const warmupUntil = parseUTC(item.warmup_until);
  const handoverExpires = parseUTC(item.handover_expires_at);

  if (item.status === 'activating' && warmupUntil) {
    const rem = Math.max(0, warmupUntil - nowMs);
    return (
      <span style={{ fontFamily: 'monospace', color: rem > 0 ? '#fd7e14' : '#198754' }}>
        {rem > 0 ? `warmup ${fmtElapsed(rem)}` : 'confirming…'}
      </span>
    );
  }
  if (item.status === 'handing_over' && handoverExpires) {
    const rem = Math.max(0, handoverExpires - nowMs);
    return (
      <span style={{ fontFamily: 'monospace', color: '#ffc107' }}>
        handover {fmtElapsed(rem)}
      </span>
    );
  }
  if (item.status === 'active' && item._remainingSeconds != null) {
    const startedAt = parseUTC(item.started_at);
    if (startedAt) {
      const elapsed = nowMs - startedAt;
      const holdMs = item._remainingSeconds * 1000 - elapsed;
      const rem = Math.max(0, holdMs);
      return (
        <span style={{ fontFamily: 'monospace', color: '#198754' }}>
          hold {fmtElapsed(rem)}
        </span>
      );
    }
  }
  return <span style={{ color: '#adb5bd' }}>—</span>;
}

function ElapsedCell({ item, nowMs }) {
  const base =
    item.status === 'queued'
      ? parseUTC(item.requested_at)
      : parseUTC(item.started_at) ?? parseUTC(item.requested_at);
  if (!base) return <span style={{ color: '#adb5bd' }}>—</span>;
  const elapsed = nowMs - base;
  return (
    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
      {fmtElapsed(elapsed)}
    </span>
  );
}

function EventLog({ entries }) {
  if (!entries || entries.length === 0)
    return <span style={{ color: '#adb5bd', fontSize: '11px' }}>no events yet</span>;

  return (
    <div
      style={{
        fontFamily: 'monospace',
        fontSize: '11px',
        lineHeight: '1.6',
        maxHeight: '80px',
        overflowY: 'auto',
        borderLeft: '3px solid #dee2e6',
        paddingLeft: '6px',
      }}
    >
      {entries.map((e, i) => (
        <div key={i} style={{ color: e.color ?? '#495057', whiteSpace: 'nowrap' }}>
          <span style={{ color: '#adb5bd' }}>{e.time}</span> <span>{e.emoji} {e.label}</span>
          {e.note && <span style={{ color: '#6c757d' }}> — {e.note}</span>}
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ item }) {
  const phase = getPhase(item);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <PortActivationBadge
        statusEntry={activationEntryFromBatchStatus(item.status)}
        compact
        showTrafficLight={false}
      />
      <span
        style={{
          fontSize: '11px',
          color: phase.color,
          whiteSpace: 'nowrap',
        }}
        title={phase.label}
      >
        {phase.emoji} {phase.label}
      </span>
    </span>
  );
}

export default function ActivationQueuePanel({ gatewayId, isOpen, onToggle }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [expandedLog, setExpandedLog] = useState(null);

  const transitionLogRef = useRef({});
  const lastStatusRef = useRef({});
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isOpen]);

  const recordTransitions = useCallback((rows) => {
    const log = transitionLogRef.current;
    const last = lastStatusRef.current;
    const now = new Date();
    const timeStr = now.toLocaleTimeString('en-GB', { hour12: false });

    rows.forEach((item) => {
      const prev = last[item.id]?.status;
      const curr = item.status;
      if (!log[item.id]) log[item.id] = [];

      if (!prev) {
        log[item.id].push({
          time: timeStr,
          emoji: '📥',
          label: `queued (port ${item.target_port_id})`,
          note: `group ${item.port_group}`,
          color: '#6c757d',
        });
      } else if (prev !== curr) {
        const phase = getPhase(item);
        const colors = {
          queued: '#6c757d',
          activating: '#fd7e14',
          active: '#198754',
          handing_over: '#ffc107',
        };
        log[item.id].push({
          time: timeStr,
          emoji: phase.emoji,
          label: `→ ${curr}`,
          note:
            curr === 'activating'
              ? `warmup until ${fmtTime(item.warmup_until)}`
              : curr === 'active'
                ? 'confirmed'
                : curr === 'handing_over'
                  ? `handover expires ${fmtTime(item.handover_expires_at)}`
                  : '',
          color: colors[curr] ?? '#495057',
        });
      }

      if (log[item.id].length > MAX_LOG_ENTRIES) {
        log[item.id] = log[item.id].slice(-MAX_LOG_ENTRIES);
      }

      last[item.id] = {
        status: curr,
        _remainingSeconds: last[item.id]?._remainingSeconds ?? item._remainingSeconds ?? null,
      };
    });

    const currentIds = new Set(rows.map((r) => r.id));
    Object.keys(last).forEach((id) => {
      const numId = Number(id);
      if (!currentIds.has(numId) && last[id]?.status && last[id].status !== '_done') {
        if (!log[id]) log[id] = [];
        log[id].push({ time: timeStr, emoji: '✅', label: '→ done / removed', color: '#198754' });
        last[id] = { status: '_done' };
      }
    });
  }, []);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await api.get('/api/activation-queue');
      let rows = res?.data?.items ?? [];
      if (gatewayId) {
        rows = rows.filter(
          (r) =>
            !r.gateway_id ||
            r.gateway_id === gatewayId ||
            r.gateway_id === Number(gatewayId)
        );
      }
      rows = rows.map((r) => ({
        ...r,
        _remainingSeconds: lastStatusRef.current[r.id]?._remainingSeconds ?? null,
      }));
      setItems(rows);
      setLastFetch(new Date());
      setError(null);
      recordTransitions(rows);
    } catch (e) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [gatewayId, recordTransitions]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    fetchQueue();
    const id = setInterval(fetchQueue, POLL_MS);
    return () => clearInterval(id);
  }, [isOpen, fetchQueue]);

  useEffect(() => {
    if (!isOpen) return;
    const unsub = api.subscribeTo('activation_update', (data) => {
      if (!data?.port_id) return;
      if (data.remaining_seconds != null) {
        const current = itemsRef.current;
        const match = current.find((r) => r.target_port_id === data.port_id);
        if (match) {
          const last = lastStatusRef.current;
          if (!last[match.id]) last[match.id] = {};
          last[match.id]._remainingSeconds = data.remaining_seconds;
        }
      }
      fetchQueue();
    });
    return () => typeof unsub === 'function' && unsub();
  }, [isOpen, fetchQueue]);

  function getElapsedMs(item) {
    const base =
      item.status === 'queued'
        ? parseUTC(item.requested_at)
        : parseUTC(item.started_at) ?? parseUTC(item.requested_at);
    return base ? nowMs - base : 0;
  }

  const totalActive = items.filter((i) => i.status === 'active').length;
  const totalActivating = items.filter((i) => i.status === 'activating').length;
  const totalQueued = items.filter((i) => i.status === 'queued').length;
  const totalHandover = items.filter((i) => i.status === 'handing_over').length;
  const stuckCount = items.filter((i) => getStuck(i, getElapsedMs(i)) != null).length;

  return (
    <div className="card mb-3" style={{ border: '1px solid #dee2e6' }}>
      <div
        className="card-header d-flex justify-content-between align-items-center py-2"
        style={{
          cursor: 'pointer',
          background: 'linear-gradient(90deg, #1a1a2e 0%, #16213e 100%)',
          borderLeft: '4px solid #0f3460',
          color: '#e2e8f0',
        }}
        onClick={onToggle}
      >
        <div className="d-flex align-items-center gap-3">
          <strong style={{ fontSize: '14px', letterSpacing: '0.03em' }}>
            {isOpen ? '▼' : '▶'} Activation Queue
          </strong>
          {isOpen && items.length > 0 && (
            <div className="d-flex gap-2" style={{ fontSize: '11px' }}>
              {totalQueued > 0 && (
                <span
                  style={{
                    background: '#6c757d33',
                    color: '#adb5bd',
                    padding: '1px 7px',
                    borderRadius: '999px',
                    border: '1px solid #6c757d55',
                  }}
                >
                  ⏳ {totalQueued} queued
                </span>
              )}
              {totalActivating > 0 && (
                <span
                  style={{
                    background: '#fd7e1433',
                    color: '#fd7e14',
                    padding: '1px 7px',
                    borderRadius: '999px',
                    border: '1px solid #fd7e1455',
                  }}
                >
                  🟡 {totalActivating} activating
                </span>
              )}
              {totalActive > 0 && (
                <span
                  style={{
                    background: '#19875433',
                    color: '#51cf66',
                    padding: '1px 7px',
                    borderRadius: '999px',
                    border: '1px solid #19875455',
                  }}
                >
                  🟢 {totalActive} active
                </span>
              )}
              {totalHandover > 0 && (
                <span
                  style={{
                    background: '#ffc10733',
                    color: '#ffd43b',
                    padding: '1px 7px',
                    borderRadius: '999px',
                    border: '1px solid #ffc10755',
                  }}
                >
                  🔁 {totalHandover} handover
                </span>
              )}
              {stuckCount > 0 && (
                <span
                  style={{
                    background: '#dc354533',
                    color: '#ff6b6b',
                    padding: '1px 7px',
                    borderRadius: '999px',
                    border: '1px solid #dc354555',
                    fontWeight: 700,
                  }}
                >
                  ⚠️ {stuckCount} stuck
                </span>
              )}
            </div>
          )}
        </div>
        <span style={{ fontSize: '11px', color: '#718096' }}>
          {isOpen
            ? lastFetch
              ? `updated ${lastFetch.toLocaleTimeString('en-GB', { hour12: false })}`
              : 'loading…'
            : 'click to inspect queue'}
        </span>
      </div>

      {isOpen && (
        <div className="card-body p-0">
          {error && (
            <div className="alert alert-danger py-2 m-2 mb-0" style={{ fontSize: '12px' }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {loading && items.length === 0 && (
            <div className="text-muted p-3" style={{ fontSize: '12px' }}>
              <span className="spinner-border spinner-border-sm me-2" />
              Loading queue…
            </div>
          )}

          {!loading && items.length === 0 && !error && (
            <div className="p-3 text-center" style={{ color: '#adb5bd', fontSize: '13px' }}>
              <span style={{ fontSize: '24px', display: 'block', marginBottom: '4px' }}>✅</span>
              No activations in progress
            </div>
          )}

          {items.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '12px',
                  fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                }}
              >
                <thead>
                  <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    {['Port', 'Group', 'Number', 'Status / Phase', 'Elapsed', 'Countdown', 'Queued at', 'Event Log'].map(
                      (h) => (
                        <th
                          key={h}
                          style={{
                            padding: '6px 10px',
                            textAlign: 'left',
                            fontFamily: 'system-ui, sans-serif',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#6c757d',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const elapsedMs = getElapsedMs(item);
                    const stuck = getStuck(item, elapsedMs);
                    const phase = getPhase(item);
                    const logEntries = transitionLogRef.current[item.id] ?? [];
                    const isLogOpen = expandedLog === item.id;

                    return (
                      <React.Fragment key={item.id}>
                        <tr
                          style={{
                            background: rowBg(stuck),
                            borderBottom: isLogOpen ? 'none' : '1px solid #f0f0f0',
                            borderLeft:
                              stuck === 'crit'
                                ? '3px solid #dc3545'
                                : stuck === 'warn'
                                  ? '3px solid #fd7e14'
                                  : '3px solid transparent',
                            transition: 'background 0.2s',
                          }}
                        >
                          <td style={{ padding: '7px 10px', fontWeight: 600 }}>{item.target_port_id}</td>
                          <td style={{ padding: '7px 10px', color: '#6c757d' }}>
                            gw{item.gateway_id ?? 1}·g{item.port_group}
                          </td>
                          <td style={{ padding: '7px 10px', color: '#495057' }}>{item.number ?? '—'}</td>
                          <td style={{ padding: '7px 10px' }}>
                            <div className="d-flex flex-column gap-1">
                              <StatusBadge item={item} />
                              <span style={{ fontSize: '10px', color: phase.color, paddingLeft: '2px' }}>
                                {phase.label}
                              </span>
                              {stuck === 'crit' && (
                                <span style={{ fontSize: '10px', color: '#dc3545', fontWeight: 700 }}>
                                  ⚠️ STUCK — check logs
                                </span>
                              )}
                              {stuck === 'warn' && (
                                <span style={{ fontSize: '10px', color: '#fd7e14', fontWeight: 600 }}>
                                  ⏱ Taking longer than usual
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '7px 10px' }}>
                            <ElapsedCell item={item} nowMs={nowMs} />
                          </td>
                          <td style={{ padding: '7px 10px' }}>
                            <CountdownCell item={item} nowMs={nowMs} />
                          </td>
                          <td style={{ padding: '7px 10px', color: '#6c757d', fontSize: '11px' }}>
                            {fmtTime(item.requested_at)}
                          </td>
                          <td style={{ padding: '7px 10px' }}>
                            <button
                              type="button"
                              onClick={() => setExpandedLog(isLogOpen ? null : item.id)}
                              style={{
                                background: 'none',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                color: '#495057',
                                fontFamily: 'system-ui, sans-serif',
                              }}
                            >
                              {isLogOpen ? '▲ hide' : `▼ log (${logEntries.length})`}
                            </button>
                          </td>
                        </tr>
                        {isLogOpen && (
                          <tr
                            style={{
                              background: '#f8f9fa',
                              borderBottom: '1px solid #dee2e6',
                              borderLeft:
                                stuck === 'crit'
                                  ? '3px solid #dc3545'
                                  : stuck === 'warn'
                                    ? '3px solid #fd7e14'
                                    : '3px solid #dee2e6',
                            }}
                          >
                            <td colSpan={8} style={{ padding: '8px 16px 12px' }}>
                              <div
                                style={{
                                  marginBottom: '4px',
                                  fontSize: '11px',
                                  color: '#6c757d',
                                  fontFamily: 'system-ui, sans-serif',
                                }}
                              >
                                Activation #{item.id} — state transitions
                              </div>
                              <EventLog entries={logEntries} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div
            style={{
              borderTop: '1px solid #f0f0f0',
              padding: '6px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#fafafa',
              fontSize: '11px',
              color: '#adb5bd',
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            <span>
              {items.length} item{items.length !== 1 ? 's' : ''} · polling every {POLL_MS / 1000}s · stuck threshold:{' '}
              {STUCK_WARN_MS / 1000}s warn / {STUCK_CRIT_MS / 1000}s crit
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLoading(true);
                fetchQueue();
              }}
              disabled={loading}
              style={{
                background: 'none',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '2px 10px',
                fontSize: '11px',
                cursor: loading ? 'not-allowed' : 'pointer',
                color: '#6c757d',
                fontFamily: 'system-ui, sans-serif',
              }}
            >
              {loading ? '⟳ refreshing…' : '⟳ refresh now'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
